import clsx from 'clsx';
import { Anchor, Button, Icon, Referenz } from 'components/atoms';
import { ContentRow } from 'components/molecules';
import { NamensTimeline } from 'components/profiles/index';
import React from 'react';
import type { UnternehmenDto, UnternehmenPlusDto } from 'types/unternehmen';
import { getGoogleMapsLink, scrollToAnchor } from 'utils';
import { useTranslation } from 'utils/hooks';

import css from './Adresse.module.scss';
import Map from './Map';

type AdresseProps = {
  unternehmen: UnternehmenDto | UnternehmenPlusDto;
};

const Adresse: React.FC<AdresseProps> = ({ unternehmen }) =>
{
  const adresse = unternehmen.adresse;

  const { t, r } = useTranslation(['profiles']);

  const isPlusPage = unternehmen.tier === 'Plus';
  const adressen = 'adressen' in unternehmen ? unternehmen.adressen : [];
  const unternehmenAnAdresse = 'unternehmenAnAdresse' in unternehmen ? unternehmen.unternehmenAnAdresse : [];
  const hasHistorischeAdressen = adressen.length > 1;


  const premiumButton = (
    <div style={{ margin: '0.5rem 0' }}>
      <Button
        variant="secondary"
        onClick={() => scrollToAnchor('plus-inhalte', -100)}
      >
      <Icon name={'lock'}/>
      {t('profiles:unternehmen.adressen.historischCta')}
    </Button>
  </div>);

  const currentAdresse = adresse === null ? null : (
    <ContentRow
      title={t('profiles:address.label')}
      actionRenderer={!isPlusPage ? () => premiumButton : undefined}
      groupItem={isPlusPage}
    >
      <div className={clsx(css.content, isPlusPage && css.plusContent)}>
        <div className={css.actionWrapper}>
          <Anchor
            href={getGoogleMapsLink(adresse)}
            target="_blank"
            external
          >
            {adresse.full}
          </Anchor>
          {adresse.plz && adresse.straße && adresse.länderCode == 'AUT' && (<Anchor
            className={css.ctaButton}
            as='button'
            asVariant='secondary'
            href={`https://grundbuch.auszug.at/search?q=${encodeURIComponent(adresse.straße + ', ' + adresse.plz)}`}
            target="_blank">
            {t('profiles:unternehmen.adresse.cta')}
          </Anchor>)}
        </div>
        <Map href={getGoogleMapsLink(adresse)}/>
        {!isPlusPage && (<button
          className={css.premiumButton}
          onClick={() => scrollToAnchor('plus-inhalte', -100)}
        >
          <Icon name={'lock'}/>
          <div>{r('profiles:unternehmen.adressen.weitereCta')}</div>
        </button>)}
      </div>
    </ContentRow>
  );

  const unternehmenAnDieserAdresse = !isPlusPage || !unternehmenAnAdresse ? null : (
    <ContentRow
      title={r('profiles:unternehmen.adressen.anDieserAdresseLabel')}
      groupItem={isPlusPage && hasHistorischeAdressen}
    >
      <div className={css.weitereProfile}>
        <ul>
        {unternehmenAnAdresse.sort((a, b)=>a.name.localeCompare(b.name)).map((ref) =>
          (
            <li key={ref.id}>
              <Referenz data={ref}>
                {({ iconName, href }) => (
                  <Anchor
                    href={href}
                    className={css.content}
                  >
                    <Icon name={iconName} />
                    <span className={css.name}>{ref.name}</span>
                  </Anchor>
                )}
              </Referenz>
            </li>
          ),
        )}
        </ul>
        <div className={css.searchCta}>
        <a
          href={'/suche/d?&adresse=' + adresse.normalized}
          target={'_blank'}
        >
          {t('profiles:unternehmen.adressen.searchCta')}
        </a>
        </div>
      </div>
    </ContentRow>
  );

  const historischeAdressen = !isPlusPage || !hasHistorischeAdressen ? null : (
      <ContentRow
        title={t('profiles:unternehmen.adressen.label')}
      >
        <NamensTimeline
          items={[...adressen].reverse().map((timelineAdresse) => ({
            zeitpunkt: timelineAdresse.zeitpunkt,
            text: timelineAdresse.item?.full ?? t('profiles:unternehmen.adressen.unbekannt'),
          }))}
          change={t('profiles:unternehmen.adressen.change')}
        />
      </ContentRow>
  );

  return (
    <>
      {currentAdresse}
      {unternehmenAnDieserAdresse}
      {historischeAdressen}
    </>
  );
};

export default Adresse;